/**
 * Configurable PBI review floors for the exam plan.
 *
 * Psychometric practice (Ebel, OnDataSuite, Assessment Systems) treats
 * point-biserial / item-total correlation cutoffs as rules of thumb, not laws.
 * Common review bands:
 *
 * - Below 0.10: poor (OnDataSuite red)
 * - 0.10 to 0.19: marginal (review)
 * - 0.20 to 0.29: acceptable / Ebel marginal for D-index
 * - 0.30+: good discrimination for most licensing exams
 *
 * The exam plan slider picks the floor: items with PBI under that value are
 * flagged for need review alongside exposure and reuse signals.
 */

import { PBI_ADEQUATE_MIN } from "@/lib/pbi-bands"

/** Ordered lenient → strict. Values match Ebel / classroom / program tiers. */
export const PBI_REVIEW_FLOOR_OPTIONS = [
  "0.10",
  "0.15",
  "0.20",
  "0.25",
  "0.30",
  "0.35",
] as const

export type PbiReviewThreshold = (typeof PBI_REVIEW_FLOOR_OPTIONS)[number]

/** Ebel poor cutoff. Default when item stats exist but no prior plan is saved. */
export const PBI_REVIEW_FLOOR_DEFAULT: PbiReviewThreshold = "0.20"

/** Seeded demo plan uses the same floor as PBI band "adequate" in the product. */
export const PBI_REVIEW_FLOOR_PROGRAM: PbiReviewThreshold =
  PBI_ADEQUATE_MIN.toFixed(2) as PbiReviewThreshold

export function isPbiReviewThreshold(value: string): value is PbiReviewThreshold {
  return (PBI_REVIEW_FLOOR_OPTIONS as readonly string[]).includes(value)
}

export function normalizePbiReviewThreshold(
  value: string | undefined | null,
): PbiReviewThreshold {
  if (value && isPbiReviewThreshold(value)) return value
  return PBI_REVIEW_FLOOR_DEFAULT
}

export function pbiReviewFloorFromThreshold(threshold: PbiReviewThreshold): number {
  return Number(threshold)
}

export function pbiReviewThresholdIndex(threshold: PbiReviewThreshold): number {
  const index = PBI_REVIEW_FLOOR_OPTIONS.indexOf(threshold)
  return index >= 0 ? index : PBI_REVIEW_FLOOR_OPTIONS.indexOf(PBI_REVIEW_FLOOR_DEFAULT)
}

export function pbiReviewThresholdFromIndex(index: number): PbiReviewThreshold {
  return (
    PBI_REVIEW_FLOOR_OPTIONS[index] ??
    PBI_REVIEW_FLOOR_DEFAULT
  )
}

export function formatPbiReviewFloorLabel(threshold: PbiReviewThreshold): string {
  return Number(threshold).toFixed(2)
}

export function pbiReviewFloorSummary(threshold: PbiReviewThreshold): string {
  return `Flag below ${formatPbiReviewFloorLabel(threshold)}`
}
